// Browser Desk console.
//
// One row per vendor login the browser desk runs (browser automations that used to run
// inside the worker). The question each row answers is "did it run today, and if not,
// what does a person need to do?" — so the status column carries the desk's own reason
// (turned off after a lockout, stopped for a vendor outage, never ran), not just a colour.
//
// The page: a summary line, a "needs a look" banner (the 06:20 check and current failures),
// one card per desk, then the logins as a table or the Today timeline. A row click (or
// Details) opens the login's drawer (LoginDrawer): lifecycle, jobs, sign-in budget, runs with
// triage and Promote. Every action that changes a desk asks first, in a dialog: Run now
// (RunNowDialog), Schedule (ScheduleDialog), Promote (PromoteDialog), and the reason-taking
// ones (ReasonDialog). Clearing a "verify it's you" challenge happens in the desk's live
// browser: Watch on a desk card, then Take over (LiveDeskDialog). ?desk=<name>&live=view|control
// opens it from an alert email; #login-<id> opens a login's drawer.
//
// Pure logic lives in deskModel.js / jobDays.js (jest). Every field the redesign added to the
// API is optional: without it the page renders as before.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, Paper, Snackbar, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LoginIcon from '@mui/icons-material/Login';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventIcon from '@mui/icons-material/Event';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HistoryIcon from '@mui/icons-material/History';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { enrollNow, getLiveMe, getLogins, notThere, resumeToday } from './api';
import AuditDialog from './AuditDialog';
import { SignInsCell } from './BudgetMeter';
import BrowserDeskHelp from './BrowserDeskHelp';
import { DeskCard, DevCard, LoginLine } from './DeskCards';
import {
    BAD, ENROLL_TIP, WARN, bannerItems, daysText, deskLabel, deskList, deskOrder, deskPod, deskStrip, driftNotice,
    dueJobTypes, fmtTime, isOffice, jobLabel, lastLiveRun, loginDesk, loginFromHash, needsEnroll, needsLook, pendingAt,
    probationOf, prodName, prodOrdinal, scheduleOf, statusOf, testRunsToday,
} from './deskModel';
import { dueToday, jobDayLines, lastRunKind, sourcesText } from './jobDays';
import LiveDeskDialog from './LiveDeskDialog';
import LoginDrawer from './LoginDrawer';
import PromoteDialog from './PromoteDialog';
import ReasonDialog from './ReasonDialog';
import RunNowDialog from './RunNowDialog';
import ScheduleDialog from './ScheduleDialog';
import ScreenshotsDialog from './ScreenshotsDialog';
import TodayTimeline from './TodayTimeline';
import { Tag, WARN_TEXT, useTones } from './tones';

const REFRESH_MS = 60 * 1000;

const VIEW_KEY = 'browserDesk.view';
const readView = () => {
    try { return localStorage.getItem(VIEW_KEY) === 'today' ? 'today' : 'table'; } catch { return 'table'; }
};
const saveView = (v) => {
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* private window: the view just isn't remembered */ }
};

// #login-<id> in the address bar while a drawer is open; nothing else in the hash.
const setHash = (id) => {
    try {
        const { pathname, search } = window.location;
        window.history.replaceState(window.history.state, '', `${pathname}${search}${id ? `#login-${id}` : ''}`);
    } catch { /* no history API: the drawer just isn't linkable */ }
};

// "Needs a look" banner: the 06:20 check's notes and today's failures; each opens its drawer.
const AttentionBanner = ({ items, morning, onOpen, onFilter, filtered }) => {
    const t = useTones();
    if (!items.length) return null;
    return (
        <Alert severity="warning" icon={false} data-attention sx={{ mb: 1.5, '& .MuiAlert-message': { width: '100%' } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }} className="d-font">
                <b>{morning ? 'Since the 06:20 check:' : `${items.length} need${items.length === 1 ? 's' : ''} a look:`}</b>
                {items.map((it) => (it.id ? (
                    <Button key={it.id} size="small" onClick={() => onOpen(it.id)}
                        sx={{ textTransform: 'none', py: 0, px: 0.75, color: t.strong, fontWeight: 400 }}>
                        <b style={{ marginRight: 4 }}>{it.id}</b> · {it.why}
                    </Button>
                ) : <span key={it.desk}>{it.why}</span>))}
                <Box sx={{ flex: 1 }} />
                <Button size="small" sx={{ textTransform: 'none' }} onClick={onFilter}>{filtered ? 'Show all' : 'Show only these'}</Button>
            </Box>
        </Alert>
    );
};

const BrowserDeskConsole = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [busy, setBusy] = useState(null);
    const [deskFilter, setDeskFilter] = useState(null); // a prod desk's ordinal, or null for all
    const [rowFilter, setRowFilter] = useState('all'); // all | look | today
    const [view, setView] = useState(readView);
    const [liveAllowed, setLiveAllowed] = useState(null); // null until /live/me answers
    const [live, setLive] = useState(null); // the desk whose live screen is open
    const [openId, setOpenId] = useState(null); // the login whose drawer is open
    const [refreshKey, setRefreshKey] = useState(0); // bumps after an action so the drawer re-reads its runs
    // Dialogs: each holds its target (a row, or { row, ... }) or null.
    const [runFor, setRunFor] = useState(null);
    const [scheduleFor, setScheduleFor] = useState(null);
    const [reasonFor, setReasonFor] = useState(null);
    const [promoteFor, setPromoteFor] = useState(null);
    const [shotsFor, setShotsFor] = useState(null);
    const [auditFor, setAuditFor] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinked = useRef(false);
    const hashRead = useRef(false);
    const t = useTones();

    const load = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        setError(null);
        try {
            setData(await getLogins());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(() => load(true), REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    // An API without /live/me (or an error) hides Watch, same as not being allowed.
    useEffect(() => {
        getLiveMe().then((d) => setLiveAllowed(Boolean(d?.allowed))).catch(() => setLiveAllowed(false));
    }, []);

    const openDrawer = useCallback((id) => { setOpenId(id); setHash(id); }, []);
    const closeDrawer = useCallback(() => { setOpenId(null); setHash(null); }, []);

    // #login-<id>: open that login's drawer once the logins are in, and on later hash changes.
    useEffect(() => {
        if (!data) return undefined;
        const fromHash = () => {
            const id = loginFromHash(window.location.hash);
            if (id && (data.logins || []).some((r) => r.id === id)) setOpenId(id);
        };
        if (!hashRead.current) { hashRead.current = true; fromHash(); }
        window.addEventListener('hashchange', fromHash);
        return () => window.removeEventListener('hashchange', fromHash);
    }, [data]);

    const openLive = useCallback((name, mode, extra = {}) => {
        setLive({ desk: name, title: name === 'dev' ? 'Dev desk' : deskLabel(name), pod: deskPod(name), mode, ...extra });
    }, []);

    // ?desk=<name>&live=view|control (alert emails link here): open that desk's screen once the
    // desk list is in. Control asks first. The two params are dropped so a reload doesn't reopen it.
    useEffect(() => {
        if (deepLinked.current || !data || liveAllowed === null) return;
        const name = searchParams.get('desk');
        const want = searchParams.get('live');
        if (!name || !want) return;
        deepLinked.current = true;
        const next = new URLSearchParams(searchParams);
        next.delete('desk'); next.delete('live');
        setSearchParams(next, { replace: true });
        if (!liveAllowed) { setToast('You are not on the list of people who can open a desk\'s live screen.'); return; }
        const found = deskList(data).find((d) => d.name === name);
        if (!found) { setToast(`No desk named ${name}`); return; }
        openLive(found.name, want === 'control' ? 'control' : 'view', { pod: found.pod || deskPod(found.name), confirmFirst: true });
    }, [data, liveAllowed, searchParams, setSearchParams, openLive]);

    const afterAction = async (msg) => {
        if (msg) setToast(msg);
        setRefreshKey((k) => k + 1);
        await load(true);
    };

    // Enroll now queues the sign-in; then open that login's desk in control so the person can
    // type a code or clear a check while the desk signs in.
    const enroll = async (r) => {
        setBusy(r.id);
        try {
            const res = await enrollNow(r.id);
            setToast(res?.message || 'Queued: the desk signs it in and records its device (no files)');
            if (liveAllowed && !isOffice(r, data)) {
                openLive(prodName(loginDesk(r)), 'control', {
                    pod: r.desk_pod || deskPod(prodName(loginDesk(r))),
                    note: `Sign in to ${r.vendor || r.id} here; the desk records the device when you're done.`,
                });
            }
            await load(true);
        } catch (e) {
            setToast(e.message);
        } finally {
            setBusy(null);
        }
    };

    // One entry point for every action button, in a row or in the drawer.
    const act = (row, kind, extra = {}) => {
        switch (kind) {
            case 'open': return openDrawer(row.id);
            case 'run': return setRunFor(row);
            case 'schedule': return setScheduleFor(row);
            case 'enroll': return enroll(row);
            case 'promote': return setPromoteFor({ row, run: extra.run });
            case 'shot': return setShotsFor({ login: row, shot: extra.shot });
            case 'shots': return setShotsFor({ login: row });
            case 'audit': return setAuditFor({ login: row.id });
            case 'resume':
                return resumeToday(row.id).then(() => afterAction(`${row.id}: retries allowed again today`))
                    .catch((e) => setToast(notThere(e) ? 'This needs the newer Browser Desk API.' : e.message));
            default: return setReasonFor({ kind, row, ...extra }); // off, on, stop, skip, fix, release
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }
    if (error) {
        // The two expected "not ready yet" states get a plain explanation instead of a raw
        // HTTP message; anything else is a real error.
        if (/not published its registry/i.test(error)) {
            return (
                <Alert severity="info" sx={{ mt: 1 }}>
                    No desk logins to show yet. The browser desk publishes its list of logins when it
                    starts, so they appear here after the next desk deploy.
                </Alert>
            );
        }
        if (/^not found$/i.test(error.trim())) {
            return (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    The Browser Desk service is not deployed yet (Integrations microservice,
                    /browser-desk). This page fills in once it is.
                </Alert>
            );
        }
        return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
    }

    const nowMs = Date.now();
    const logins = data?.logins || [];
    const desks = data?.desks || {};
    const order = deskOrder(data);
    const strip = deskStrip(data);
    const prodDesks = strip.filter((d) => prodOrdinal(d.name) !== null);
    const devDesk = strip.find((d) => d.name === 'dev');
    const filter = prodDesks.some((d) => d.prod_desk === deskFilter) ? deskFilter : null;
    const onDesk = filter === null ? logins : logins.filter((r) => loginDesk(r) === filter);
    const shown = onDesk.filter((r) => (rowFilter === 'look' ? needsLook(r, desks, order)
        : rowFilter === 'today' ? !r.disabled && r.enabled && dueJobTypes(r).length > 0 : true));
    const deskStats = (d) => {
        const mine = logins.filter((r) => loginDesk(r) === d.prod_desk);
        // A plan left over from an earlier day is not a next run.
        const dayStart = new Date().setHours(0, 0, 0, 0);
        const next = mine.map((r) => ({ id: r.id, at: pendingAt(r) })).filter((x) => x.at && new Date(x.at).getTime() >= dayStart)
            .sort((a, b) => new Date(a.at) - new Date(b.at))[0];
        // A newer API counts each desk's logins itself; an older one doesn't, so count here.
        const count = typeof d.logins === 'number' ? d.logins : mine.length;
        return { count, done: mine.filter((r) => r.today?.done).length, next };
    };
    const pickView = (v) => { if (v) { setView(v); saveView(v); } };
    const off = logins.filter((r) => r.disabled).length;
    const failing = logins.filter((r) => !r.disabled && !needsEnroll(r) && statusOf(r, desks, order).colour === WARN).length;
    const toEnroll = logins.filter(needsEnroll);
    const banner = bannerItems(data);
    const openRow = openId ? logins.find((r) => r.id === openId) || null : null;
    const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Typography className="d-font" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {logins.length} login{logins.length === 1 ? '' : 's'} across {prodDesks.length} prod
                    desk{prodDesks.length === 1 ? '' : 's'}. Each login runs on exactly one prod desk; the dev desk builds and fixes.
                </Typography>
                {off > 0 && <Chip size="small" label={`${off} turned off`} sx={{ ...t.chip(BAD, '#fdecea'), fontSize: 12, height: 22 }} />}
                {failing > 0 && <Chip size="small" label={`${failing} need attention`} sx={{ ...t.chip(WARN, '#fff4e5'), fontSize: 12, height: 22 }} />}
                {toEnroll.length > 0 && (
                    <Tooltip title={ENROLL_TIP}>
                        <Chip size="small" label={`${toEnroll.length} to enroll`} sx={{ ...t.chip(WARN, '#fff4e5'), fontSize: 12, height: 22 }} />
                    </Tooltip>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography className="d-font" sx={{ fontSize: 12, color: 'text.secondary' }}>
                    updated {fmtTime(data?.generated_at)}
                </Typography>
                <Button size="small" startIcon={<RefreshIcon />} onClick={() => afterAction(null)} sx={{ textTransform: 'none' }}>
                    Refresh
                </Button>
                <Button size="small" startIcon={<HelpOutlineIcon />} onClick={() => setHelpOpen(true)} sx={{ textTransform: 'none' }}>
                    Help
                </Button>
                <Button size="small" startIcon={<HistoryIcon />} onClick={() => setAuditFor({ login: null })} sx={{ textTransform: 'none' }}>
                    Audit log
                </Button>
            </Box>

            <AttentionBanner items={banner.items} morning={banner.morning} onOpen={openDrawer}
                filtered={rowFilter === 'look'} onFilter={() => setRowFilter(rowFilter === 'look' ? 'all' : 'look')} />

            {toEnroll.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {toEnroll.map((r) => r.id).join(', ')} {toEnroll.length === 1 ? 'is' : 'are'} live but not signed in on
                    the prod desk yet, so {toEnroll.length === 1 ? 'its' : 'their'} runs are skipped. Press
                    {' '}<b>Enroll now</b> on {toEnroll.length === 1 ? 'that row' : 'each row'}: the desk signs in and records the
                    device, without pulling files. Only needed once per login.
                </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1.5, mb: 1.5 }}>
                {prodDesks.map((d) => {
                    const st = deskStats(d);
                    return (
                        <DeskCard key={d.name} desk={d} rec={desks[d.name]} count={st.count} done={st.done} next={st.next}
                            selected={filter === d.prod_desk} nowMs={nowMs}
                            onPick={() => setDeskFilter(filter === d.prod_desk ? null : d.prod_desk)}
                            onWatch={liveAllowed ? () => openLive(d.name, 'view', { pod: d.pod || deskPod(d.name) }) : null} />
                    );
                })}
                {devDesk && (
                    <DevCard desk={devDesk} rec={desks.dev} nowMs={nowMs}
                        onWatch={liveAllowed ? () => openLive('dev', 'view', { pod: devDesk.pod || deskPod('dev') }) : null} />
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography className="d-font" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    Showing{' '}
                    <b data-showing style={{ color: t.strong }}>
                        {filter === null ? 'all desks' : `Prod desk ${filter}`} · {shown.length} login{shown.length === 1 ? '' : 's'}
                    </b>
                </Typography>
                {filter !== null && (
                    <Button size="small" onClick={() => setDeskFilter(null)} sx={{ textTransform: 'none' }}>Show all desks</Button>
                )}
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup value={rowFilter} exclusive size="small" onChange={(e, v) => { if (v) setRowFilter(v); }} aria-label="Filter">
                    <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>All</ToggleButton>
                    <ToggleButton value="look" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>Needs a look</ToggleButton>
                    <ToggleButton value="today" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>Runs today</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup value={view} exclusive size="small" onChange={(e, v) => pickView(v)} aria-label="View">
                    <ToggleButton value="table" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>Table</ToggleButton>
                    <ToggleButton value="today" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>Today</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {view === 'today' ? (
                <TodayTimeline desks={desks} logins={shown}
                    prodDesks={filter === null ? prodDesks : prodDesks.filter((d) => d.prod_desk === filter)} />
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--color-border, #e0e0e0)' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['Login', 'Client', 'Data source', 'Today', 'Last live run', 'Sign-ins today', ''].map((h) => (
                                    <TableCell key={h} className="d-font" sx={{ fontSize: 12, fontWeight: 700, color: t.soft }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!shown.length && (
                                <TableRow>
                                    <TableCell colSpan={7} className="d-font" sx={{ fontSize: 13, color: t.muted }}>No logins match this view.</TableCell>
                                </TableRow>
                            )}
                            {shown.map((r) => {
                                const st = statusOf(r, desks, order);
                                const clients = [...new Set((r.jobs || []).map((j) => j.client))].join(', ');
                                // data_source is the Nexus data source name the desk publishes per job;
                                // until it does, the vendor is the closest thing. "(Mon)" = jobs on their own days.
                                const sources = sourcesText(r);
                                const sc = scheduleOf(r);
                                const label = (j) => jobLabel(j, r);
                                const jobLines = jobDayLines(r, sc.days, label);
                                const due = dueToday(r, label);
                                const prob = probationOf(r.probation);
                                const lastLive = lastLiveRun(r);
                                const tests = testRunsToday(r).length;
                                const office = isOffice(r, data);
                                const jobOverride = r.override_jobs && Object.keys(r.override_jobs).length;
                                return (
                                    <TableRow key={r.id} hover selected={openId === r.id} tabIndex={0} data-login={r.id}
                                        onClick={() => openDrawer(r.id)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) openDrawer(r.id); }}
                                        sx={{ cursor: 'pointer' }}>
                                        <TableCell sx={{ minWidth: 190 }}>
                                            <Typography className="d-font" sx={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{r.id}</Typography>
                                            {/* desk dot, sign-in kind and window (P3): one cell instead of Desk + Window columns */}
                                            <LoginLine row={r} office={office} />
                                            <Tooltip title={sc.custom ? `Set by ${sc.override.by} ${fmtTime(sc.override.at, r.timezone)}: ${sc.override.reason}` : `From the desk registry · ${r.timezone}`}>
                                                <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>
                                                    {sc.window.start}–{sc.window.end} {daysText(sc.days)}
                                                </Typography>
                                            </Tooltip>
                                            {prob && (
                                                <Tooltip title={prob.detail}>
                                                    <Chip size="small" label={prob.label} variant="outlined"
                                                        sx={{ mt: 0.5, mr: 0.5, fontSize: 11, height: 20, color: t.text(prob.colour), borderColor: t.text(prob.colour) }} />
                                                </Tooltip>
                                            )}
                                            {(sc.custom || jobOverride) ? (
                                                <Box component="span" sx={{ display: 'inline-block', mt: 0.5 }}>
                                                    <Tag label="schedule override" colour={WARN}
                                                        title={driftNotice(r, nowMs) || (sc.custom ? `Set by ${sc.override.by} ${fmtTime(sc.override.at, r.timezone)}: ${sc.override.reason}` : 'Per-job days set in the console')} />
                                                </Box>
                                            ) : null}
                                        </TableCell>
                                        <TableCell className="d-font" sx={{ fontSize: 13 }}>{clients}</TableCell>
                                        <TableCell className="d-font" sx={{ fontSize: 13 }}>
                                            {jobLines ? (
                                                <Tooltip title={<>{jobLines.map((l, i) => <div key={i}>{l}</div>)}</>}>
                                                    <span>{sources}</span>
                                                </Tooltip>
                                            ) : sources}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={st.detail || ''}>
                                                <Chip size="small" label={st.label}
                                                    sx={{ color: '#fff', bgcolor: t.fill(st.colour), fontSize: 12, height: 22 }} />
                                            </Tooltip>
                                            {due && (
                                                <Tooltip title={<>Due today:{due.names.map((n, i) => <div key={i}>{n}</div>)}</>}>
                                                    <span className="d-font" style={{ fontSize: 11, color: t.muted, marginLeft: 6 }}>
                                                        · {due.due} of {due.total} jobs
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {st.detail && (
                                                <Typography className="d-font" sx={{ fontSize: 11, color: t.muted, mt: 0.5, maxWidth: 240 }}>
                                                    {st.detail}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell className="d-font" sx={{ fontSize: 12 }}>
                                            {lastLive ? (
                                                <Tooltip title={lastLive.message || ''}>
                                                    <span style={{ color: lastLive.status === 'completed' ? t.ok : t.warn }}>
                                                        {lastLive.status} · {fmtTime(lastLive.at, r.timezone)}
                                                        {lastLive === r.last_run ? lastRunKind(r) : lastLive.adhoc ? ' (on request)' : ''}
                                                    </span>
                                                </Tooltip>
                                            ) : '—'}
                                            {tests > 0 && (
                                                <Typography className="d-font" sx={{ fontSize: 11, color: t.muted, mt: 0.25 }}>
                                                    +{tests} test run{tests === 1 ? '' : 's'} today
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell><SignInsCell row={r} /></TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            <Box data-actions sx={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25,
                                                '& .MuiButton-root': { whiteSpace: 'nowrap' },
                                            }}>
                                                {needsEnroll(r) ? (
                                                    <Tooltip title={ENROLL_TIP}>
                                                        <span>
                                                            <Button size="small" variant="contained" color="warning" startIcon={<LoginIcon />}
                                                                sx={{ textTransform: 'none', mr: 0.5, ...(t.dark ? {} : { bgcolor: WARN_TEXT, '&:hover': { bgcolor: '#92400e' } }) }}
                                                                disabled={busy === r.id}
                                                                onClick={stop(() => enroll(r))}>
                                                                Enroll now
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title={r.disabled ? 'Turn it on first'
                                                        : r.fixing ? 'Being fixed on the dev desk; prod runs it again once it is released'
                                                            : !r.enabled && !office ? 'Not live yet: tested on the dev desk'
                                                                : 'Pick Live or Test and the jobs; it says what it will do before queuing'}>
                                                        <span>
                                                            <Button size="small" startIcon={<PlayArrowIcon />} sx={{ textTransform: 'none' }}
                                                                disabled={Boolean(r.disabled) || Boolean(r.fixing) || (!r.enabled && !office) || busy === r.id}
                                                                onClick={stop(() => act(r, 'run'))}>
                                                                Run now…
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {r.disabled ? (
                                                    <Button size="small" color="success" startIcon={<PowerSettingsNewIcon />}
                                                        sx={{ textTransform: 'none' }} onClick={stop(() => act(r, 'on'))}>
                                                        Turn on…
                                                    </Button>
                                                ) : (
                                                    <Button size="small" color="error" startIcon={<PowerSettingsNewIcon />}
                                                        sx={{ textTransform: 'none' }} onClick={stop(() => act(r, 'off'))}>
                                                        Turn off…
                                                    </Button>
                                                )}
                                                <Button size="small" startIcon={<EventIcon />} sx={{ textTransform: 'none' }}
                                                    onClick={stop(() => act(r, 'schedule'))}>
                                                    Schedule…
                                                </Button>
                                                <Button size="small" startIcon={<MenuOpenIcon />} sx={{ textTransform: 'none' }}
                                                    onClick={stop(() => openDrawer(r.id))}>
                                                    Details
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <LoginDrawer login={openRow} data={data} desks={desks} refreshKey={refreshKey}
                onClose={closeDrawer} act={(kind, extra) => act(openRow, kind, extra)} />
            <BrowserDeskHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
            <RunNowDialog login={runFor} data={data} onClose={() => setRunFor(null)}
                onDone={(msg) => { setRunFor(null); afterAction(msg); }} />
            <ScheduleDialog login={scheduleFor} onClose={() => setScheduleFor(null)}
                onDone={(msg) => { setScheduleFor(null); afterAction(msg); }} />
            <ReasonDialog target={reasonFor} onClose={() => setReasonFor(null)}
                onDone={(msg) => { setReasonFor(null); afterAction(msg); }} />
            <PromoteDialog target={promoteFor} onClose={() => setPromoteFor(null)} onDone={(msg) => afterAction(msg)} />
            <ScreenshotsDialog target={shotsFor} onClose={() => setShotsFor(null)} />
            <AuditDialog target={auditFor} onClose={() => setAuditFor(null)} />
            <LiveDeskDialog target={live} onClose={() => setLive(null)}
                running={live && desks[live.desk] && !desks[live.desk].stale ? desks[live.desk].credential : null} />
            <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} message={toast} />
        </Box>
    );
};

export default BrowserDeskConsole;
